import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import FloorPlan from 'components/floorplan';
import ConfirmationModal from 'components/ConfirmationModal';
import { useAlert } from 'context/AlertContext';
import { validateFiles, IMAGE_ACCEPT } from 'utils/uploadValidation';

import { StepShell, HelpedBlock, FormCard, Button } from '../atoms';

const Step4 = ({
  setStep,
  showBlueprintUploadModal,
  setShowBlueprintUploadModal,
  setFloorplanImage,
}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const { setValue, watch } = useFormContext();
  const previewURL = watch('floorplanPreview');
  const createdSpaces = watch('spaces') || [];
  const existingPlacements = watch('spacePlacements') || [];
  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false);
  const [placementsError, setPlacementsError] = useState(null);

  const hasPreview = !!previewURL;
  const totalSeats = createdSpaces.reduce(
    (sum, s) => sum + (Number(s.quantity) || 0),
    0
  );

  // Ignore pins whose space was renamed or deleted in Step 3 after pinning.
  // Counting such orphans as "placed" would mask genuinely unplaced units,
  // and seeding the editor with them would push them back into the form.
  const spaceNames = new Set(createdSpaces.map((s) => s.name));
  const validPlacements = existingPlacements.filter((p) =>
    spaceNames.has(p?.spaceName)
  );

  const placedSeats = validPlacements.length;
  const hasUnplacedSeats = hasPreview && placedSeats < totalSeats;

  // Pins can exceed a space's quantity when the user goes back to Step 3
  // and lowers it after placing — those must be removed before saving.
  const overPlacedNames = createdSpaces
    .filter(
      (s) =>
        validPlacements.filter((p) => p.spaceName === s.name).length >
        (Number(s.quantity) || 0)
    )
    .map((s) => s.name);
  const hasOverPlacedSeats = hasPreview && overPlacedNames.length > 0;

  useEffect(() => {
    if (placementsError && !hasUnplacedSeats && !hasOverPlacedSeats) {
      setPlacementsError(null);
    }
  }, [hasUnplacedSeats, hasOverPlacedSeats, placementsError]);

  const handleSave = () => {
    if (hasUnplacedSeats) {
      setPlacementsError(t('event_create.placements_required'));
      return;
    }
    if (hasOverPlacedSeats) {
      setPlacementsError(
        t('event_create.placements_overflow', {
          spaces: overPlacedNames.join(', '),
        })
      );
      return;
    }
    setStep(5);
  };

  const revokePreviewIfBlob = () => {
    if (typeof previewURL === 'string' && previewURL.startsWith('blob:')) {
      URL.revokeObjectURL(previewURL);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const { accepted, errors: uploadErrors } = validateFiles([file], {
      kind: 'image',
      t,
    });
    if (uploadErrors.length) {
      showAlert({
        type: 'warning',
        title: t('upload.error_title', { defaultValue: 'Upload issue' }),
        message: uploadErrors.join('\n'),
        primaryAction: { label: t('common.ok') },
      });
      e.target.value = '';
      return;
    }
    if (!accepted.length) return;
    revokePreviewIfBlob();
    const url = URL.createObjectURL(file);
    setValue('floorplanPreview', url);
    setValue('spacePlacements', []);
    setValue('createFloorplan', true);
    if (typeof setFloorplanImage === 'function') {
      setFloorplanImage(file);
    }
    setShowBlueprintUploadModal(false);
    e.target.value = '';
  };

  const handleSkip = () => {
    revokePreviewIfBlob();
    setValue('floorplanPreview', null);
    setValue('spacePlacements', []);
    setValue('createFloorplan', false);
    if (typeof setFloorplanImage === 'function') {
      setFloorplanImage(null);
    }
    setShowSkipConfirmation(false);
    setStep(5);
  };

  return (
    <StepShell
      eyebrow={t('event_create.step4_eyebrow', 'Step 04 / Floor plan')}
      title={
        hasPreview
          ? t('event_create.step4_title_editor', 'Place each space on the plan')
          : t('event_create.step4_title_prompt', "Show brands where they'll be")
      }
      lede={
        hasPreview
          ? t(
              'event_create.step4_lede_editor',
              'Pick a space from the list, then click anywhere on the plan to drop a pin. You can re-pin or remove anytime.'
            )
          : t(
              'event_create.step4_lede_prompt',
              'Optional, but a labeled floor plan boosts bookings noticeably. Upload a sketch, blueprint, or even a phone photo of a printed map.'
            )
      }
    >
      {!hasPreview ? (
        <HelpedBlock sectionKey="floorplanUpload">
          <FormCard
            title={t('event_create.upload_floor_plan', 'Upload your floor plan')}
          >
            <button
              type="button"
              onClick={() => setShowBlueprintUploadModal(true)}
              className="w-full py-16 rounded-2xl border border-dashed border-line bg-paper-2 hover:bg-paper transition grid place-items-center"
            >
              <span className="flex flex-col items-center gap-3 text-gray-500">
                <span className="w-14 h-14 grid place-items-center rounded-2xl bg-white border border-line shadow-card text-ink-strong">
                  <ArrowUpTrayIcon className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-[15px] font-bold text-ink-strong">
                    {t(
                      'event_create.drop_floor_plan',
                      'Drop a floor plan here'
                    )}
                  </span>
                  <span className="block field-helper mt-0.5">
                    {t(
                      'event_create.floor_plan_formats',
                      'PNG or JPG · up to 20MB'
                    )}
                  </span>
                </span>
                <span className="mono text-[10.5px] uppercase tracking-widest text-muted">
                  {t('event_create.or_click_to_browse', 'or click to browse')}
                </span>
              </span>
            </button>
          </FormCard>

          <div className="flex justify-center">
            <Button
              variant="accent"
              size="lg"
              type="button"
              onClick={handleSkip}
            >
              {t('event_create.skip_for_now', "Skip — I don't have a floor plan")}
            </Button>
          </div>
        </HelpedBlock>
      ) : (
        <HelpedBlock sectionKey="floorplan">
          <FormCard
            title={t('event_create.floor_plan', 'Floor plan')}
            action={
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-semibold text-muted tabular-nums">
                  {placedSeats}/{totalSeats}{' '}
                  {t('event_create.placed', 'placed')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowBlueprintUploadModal(true)}
                  className="text-[11px] font-bold text-ink-strong hover:underline"
                >
                  {t('event_create.replace_floor_plan', 'Replace')}
                </button>
              </div>
            }
          >
            <FloorPlan
              img={previewURL}
              spacesList={createdSpaces}
              onPlacementsChange={(placements) =>
                setValue('spacePlacements', placements)
              }
              initialPlacements={validPlacements}
            />
            {placementsError && (
              <p
                role="alert"
                className="mt-4 text-[12.5px] text-rose-600 text-center"
              >
                {placementsError}
              </p>
            )}
          </FormCard>

          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => setShowSkipConfirmation(true)}
            >
              {t('common.skip')}
            </Button>
            <Button variant="accent" type="button" onClick={handleSave}>
              {t('common.save')}
            </Button>
          </div>
        </HelpedBlock>
      )}

      <Dialog
        open={showBlueprintUploadModal}
        onClose={() => setShowBlueprintUploadModal(false)}
      >
        <DialogBackdrop className="fixed inset-0 bg-black/50 z-40" />
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
          <DialogPanel className="bg-white p-8 rounded-2xl shadow-card w-[420px] text-center space-y-6">
            <DialogTitle className="text-[18px] font-extrabold text-ink-strong">
              {t('event_create.floor_plan_upload_title')}
            </DialogTitle>
            <label
              htmlFor="floorplan-upload"
              className="flex flex-col items-center justify-center border-2 border-dashed border-line hover:border-brand p-8 rounded-xl cursor-pointer transition bg-paper-2 hover:bg-paper"
            >
              <ArrowUpTrayIcon className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-muted text-[12.5px]">
                {t('event_create.click_or_drag')}
              </p>
              <input
                id="floorplan-upload"
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                type="button"
                onClick={() => setShowBlueprintUploadModal(false)}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <ConfirmationModal
        isOpen={showSkipConfirmation}
        title={t('event_create.skip_blueprint_title')}
        message={t('event_create.skip_blueprint_message')}
        onConfirm={handleSkip}
        onCancel={() => setShowSkipConfirmation(false)}
      />
    </StepShell>
  );
};

export default Step4;
